// drift-test:no-test-needed: additive counter+glyph changes only (added "skipped" outcome → ⊗ glyph + DROP/CXLD labels in the per-item group header). Closed-set parity with models.KnownSubCallOutcomes enforced by orchestrator/internal/contract/trace_invariants T27. Render test for the group-row strip is a follow-up.
import { useState } from "react";
import type { SubCallTrace, TraceError } from "../../lib/traceApi";
import { SubCallRow } from "./SubCallRow";

// PerItemGroupRow renders a fan-out (per-item map) sub-prompt as a single
// collapsible group with a status mini-strip in the header. Auto-expands
// when any item failed so the operator never has to discover the failure
// behind a click. Pre-this-version, a 12-item layer with gap_3 failing
// rendered as collapsed "12 instances · 4 success / 8 retried" with no
// signal that something went wrong — operator had to expand to find it.
// (Item 4 of the trace-view refactor.)
export function PerItemGroupRow({
  subPromptID,
  subCalls,
  parentStageErrors,
}: {
  subPromptID: string;
  subCalls: SubCallTrace[];
  parentStageErrors?: TraceError[];
}) {
  const successCount = subCalls.filter((sc) => sc.outcome === "success").length;
  const retriedCount = subCalls.filter((sc) => (sc.attempts ?? []).length > 1).length;
  const failCount = subCalls.filter((sc) => sc.outcome === "abort").length;
  // canceledCount = cascade-cancelled (no work done) — gray
  // droppedCount  = SkipItem terminal failure (item omitted) — amber
  const canceledCount = subCalls.filter((sc) => sc.outcome === "canceled").length;
  const droppedCount = subCalls.filter((sc) => sc.outcome === "skipped").length;
  const fallbackCount = subCalls.filter((sc) => sc.outcome === "fallback").length;
  const hasFailure = failCount + canceledCount + droppedCount + fallbackCount > 0;
  // Auto-expand on failure so the originating row + cascade victims are
  // immediately visible. Operator can still collapse manually.
  const [open, setOpen] = useState(hasFailure);
  const totalMs = subCalls.reduce((s, sc) => s + (sc.duration_ms ?? 0), 0);

  // Per-item status mini-strip — one char per sub-call, in render order
  // (which is item_key natural order after the EndLayer sort). Lets
  // operator see the failure pattern at a glance without expanding.
  // ✓ = success, ✗ = abort, ⊘ = fallback, ◌ = canceled, ⊗ = skipped.
  const strip = subCalls.map((sc) => statusGlyph(sc.outcome)).join("");

  return (
    <div className="text-sm">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 w-full text-left hover:bg-gray-800 px-2 py-1"
      >
        <span className="w-6 text-gray-500">{open ? "▼" : "▶"}</span>
        <span className="font-mono">{subPromptID}</span>
        <span className="font-mono text-[11px] ml-2" title="per-item status (✓=ok, ✗=fail, ⊘=fallback, ◌=cascade-cancelled, ⊗=dropped per SkipItem)">
          {strip.split("").map((g, i) => (
            <span key={i} className={glyphColor(g)}>{g}</span>
          ))}
        </span>
        <span className="text-gray-400 text-xs ml-auto">
          {subCalls.length} instances ·{" "}
          <span className="text-emerald-300">{successCount} OK</span>
          {failCount > 0 && <span className="text-rose-300"> / {failCount} FAIL</span>}
          {fallbackCount > 0 && <span className="text-amber-300"> / {fallbackCount} FB</span>}
          {droppedCount > 0 && <span className="text-amber-300"> / {droppedCount} DROP</span>}
          {canceledCount > 0 && <span className="text-ink-500"> / {canceledCount} CXLD</span>}
          {retriedCount > 0 && <span> / {retriedCount} retried</span>}
          {" "}· {(totalMs / 1000).toFixed(1)}s
        </span>
      </button>
      {open && (
        <div className="ml-4 space-y-1">
          {subCalls.map((sc, i) => (
            <SubCallRow key={i} subCall={sc} parentStageErrors={parentStageErrors} />
          ))}
        </div>
      )}
    </div>
  );
}

function statusGlyph(outcome: string): string {
  switch (outcome) {
    case "success": return "✓";
    case "abort": return "✗";
    case "fallback": return "⊘";
    case "canceled": return "◌";
    case "skipped": return "⊗";
    default: return "·";
  }
}

function glyphColor(g: string): string {
  switch (g) {
    case "✓": return "text-emerald-400";
    case "✗": return "text-rose-400";
    case "⊘": return "text-amber-300";
    case "⊗": return "text-amber-300";
    case "◌": return "text-ink-500";
    default: return "text-gray-500";
  }
}
